/**
 * Раздел «Социальная сфера» страницы «Аналитика» (US-20, задача DP-429) —
 * новый состав виджетов: плашка «Труд и социальная защита» из пяти плиток
 * (занятое население, безработное население, уровень безработицы, индекс
 * заработной платы, получатели АСП) и два блока — «Образование» и
 * «Здравоохранение».
 *
 * Состав US-04.4 (плашка с итогами «Всего школ / учащихся / больниц»,
 * «Самообеспеченность продуктами питания», разрез школ по видам) этим US
 * **заменён**, а не дополнен (FR-1.1).
 *
 * Весь подраздел грузится **одной операцией** `/analytics/social`:
 * разбиение на три (плашка / образование / здравоохранение) было
 * предложением US-21 FR-1.1 и бэком не реализовано — раздельные пути
 * отвечают 404. Отчётная подпись у плашки и у каждого блока при этом
 * остаётся своей, из своей части ответа: занятость публикуется раз в
 * год, образование — раз в год (с отставанием), здравоохранение — по
 * обновлению «Мониторинга СНП», и различие подписей — норма, а не
 * признак ошибки (FR-5.1, FR-5.2).
 *
 * Уровень безработицы берётся из источника готовым и **не
 * пересчитывается** из занятых и безработных (FR-2.3): методика источника
 * может отличаться от простого отношения.
 *
 * Открытые вопросы US-20, влияющие на экран, закрыты временным
 * поведением самой постановки: индекс заработной платы показывается
 * плиткой за последний доступный квартал (FR-2.4, вопрос 4), подпись
 * «Здравоохранения» строится по дате актуальности данных (FR-4.3,
 * вопрос 5), глубина drill-through — ровно то, что пришло с блоком
 * (FR-7.2, вопрос 9). Уровни КАТО не сужаются: подраздел остаётся
 * доступным на всех уровнях, как сегодня (FR-1.4, вопрос 2).
 */
import { GraduationCap, HeartPulse, PlusSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SocialCategoryBlock, type SocialCategoryRowVM } from './SocialCategoryBlock';
import {
  PeriodCaption,
  fmt,
  fmt1,
  useStatBlock,
  useStatText,
} from './statValue';
import { BlockLoader } from '../../../components/common/BlockLoader';
import { WidgetErrorBoundary } from '../../../components/common/WidgetErrorBoundary';
import {
  StatCard,
  type StatCardFootnote,
} from '../../../components/widgets/StatCard';
import { MODULES } from '../../../consts/modules';
import { useKato } from '../../../contexts/KatoContext';
import { useModulePresent } from '../../../hooks/useModulePresent';
import { getAnalyticsSocial } from '../../../requests/analytics.request';
import type {
  SocialAnalyticsResponse,
  StatValue,
} from '../../../types/analytics';

interface TileVM {
  key: string;
  title: string;
  /** null — данных нет: плитка остаётся с подписью «нет данных». */
  value: string | null;
  unit: string | null;
  footnotes: StatCardFootnote[];
}

interface SocialSectionProps {
  /** Режим витрины «Все» (US-04.1): в сводной витрине плашек нет —
   * тема показана своими виджетами (образование и здравоохранение),
   * а плашка «Труд и социальная защита» остаётся в подразделе. */
  summary?: boolean;
}

export const SocialSection = ({ summary = false }: SocialSectionProps = {}) => {
  const { t } = useTranslation();
  const { selectedKato } = useKato();
  const text = useStatText();

  // Видимость виджетов задаёт супер-администратор (US-09 FR-1):
  // выключенный виджет не рендерится вовсе и «нет данных» вместо себя не
  // показывает (FR-6.4), а соседние смыкаются (FR-1.3).
  const showPlashka = useModulePresent(MODULES.analytics.socialPlashka);
  const showEducation = useModulePresent(MODULES.analytics.socialEducation);
  const showHealthcare = useModulePresent(MODULES.analytics.socialHealthcare);

  // Подраздел приходит одним ответом: плашка и блоки не запрашиваются
  // порознь — раздельных операций у бэка нет. Запрос уходит, пока на
  // экране есть хоть один виджет раздела; выключены все — не грузим.
  const social = useStatBlock<SocialAnalyticsResponse>(
    getAnalyticsSocial,
    (!summary && showPlashka) || showEducation || showHealthcare,
  );
  const plashka = {
    data: social.data?.plashka ?? null,
    loading: social.loading,
  };
  const education = {
    data: social.data?.education ?? null,
    loading: social.loading,
  };
  const healthcare = {
    data: social.data?.healthcare ?? null,
    loading: social.loading,
  };

  /**
   * Плитка строится всегда — плашка состоит из пяти фиксированных плиток
   * (замечание пользователя, как в «Сельском хозяйстве» после DP-426).
   * Показателя нет — остаётся название и серая подпись «нет данных»
   * (режим `value: null` у `StatCard`): пропавшая плитка читается как
   * «показателя не существует», а не как «данных пока не пришло».
   * Постановка US-20 FR-2.7 предписывает обратное (плитку без величины
   * не выводить) — расхождение осознанное и подлежит отражению в US.
   */
  const tile = (
    key: string,
    title: string,
    value: StatValue | null,
    format: (v: number) => string,
    footnotes: StatCardFootnote[] = [],
  ): TileVM => {
    if (
      value === null ||
      value.state === 'no_data' ||
      value.state === 'not_applicable'
    ) {
      return { key, title, value: null, unit: null, footnotes: [] };
    }
    const shown = text(value, format);
    return {
      key,
      title,
      value: shown.text,
      unit: shown.known ? value.unit : null,
      footnotes: shown.known ? footnotes : [],
    };
  };

  const data = plashka.data;
  const wage = data?.wage_index ?? null;

  const tiles: TileVM[] = [
    tile(
      'employed',
      t('analytics.social.plashka.employed'),
      data?.employed ?? null,
      fmt,
    ),
    tile(
      'unemployed',
      t('analytics.social.plashka.unemployed'),
      data?.unemployed ?? null,
      fmt,
    ),
    // Уровень безработицы — значение источника, не отношение занятых и
    // безработных (FR-2.3).
    tile(
      'unemploymentRate',
      t('analytics.social.plashka.unemploymentRate'),
      data?.unemployment_rate ?? null,
      fmt1,
    ),
    // Индекс заработной платы — квартальная величина со своим периодом
    // внутри годовой плашки; база сравнения приходит подписью (FR-2.4).
    tile(
      'wageIndex',
      t('analytics.social.plashka.wageIndex'),
      wage?.value ?? null,
      fmt1,
      [wage?.base_label, wage?.period?.label]
        .filter((part): part is string => part != null && part !== '')
        .map((part) => ({ text: part })),
    ),
    tile(
      'aspRecipients',
      t('analytics.social.plashka.aspRecipients'),
      data?.asp_recipients ?? null,
      fmt,
      [{ text: t('analytics.social.plashka.aspNote') }],
    ),
  ];

  const educationRows: SocialCategoryRowVM[] = (education.data?.rows ?? []).map(
    (row, index) => ({
      key: row.code ?? String(index),
      name: row.name ?? '—',
      // Смысл численности различается по категориям и приходит из ответа
      // (FR-3.2): «численность детей», «численность учащихся школ», …
      hint: row.students_label,
      values: [row.count, row.students],
      details: row.details,
    }),
  );

  const healthcareRows: SocialCategoryRowVM[] = (
    healthcare.data?.rows ?? []
  ).map((row, index) => ({
    key: row.code ?? String(index),
    name: row.name ?? '—',
    values: [row.count],
    details: row.details,
  }));

  return (
    <section className="space-y-4" data-testid="analytics-social">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('analytics.social.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <HeartPulse
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* Плашка — только в тематическом подразделе: в сводной витрине
          «Все» плашек нет, темы показаны виджетами. */}
      {!summary && showPlashka && (
        <WidgetErrorBoundary key={`social-plashka-${selectedKato ?? 'none'}`}>
          {plashka.loading ? (
            <BlockLoader />
          ) : (
            <div className="space-y-2.5" data-testid="social-plashka">
              {/* Подпись плашки относится только к ней: у блоков ниже
                  периоды свои (FR-2.6, FR-5.1). */}
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h3 className="text-sm font-extrabold">
                  {t('analytics.social.plashka.title')}
                </h3>
                <PeriodCaption
                  period={data?.period ?? null}
                  testId="social-plashka-period"
                />
              </div>
              {/* Плиток всегда пять: без данных плитка остаётся на месте
                  с названием и подписью «нет данных» — ряд не
                  перестраивается от того, что выпуск ещё не вышел. */}
              <div className="flex flex-wrap gap-3.5">
                {tiles.map((card) => (
                  // min-w-40, а не обычные min-w-52: пять плиток должны
                  // встать в один ряд уже на 1280, где под контент
                  // остаётся ~924 px.
                  <div key={card.key} className="min-w-40 flex-1">
                    <StatCard
                      testId={`analytics-card-social-${card.key}`}
                      title={card.title}
                      value={card.value}
                      unit={card.unit}
                      footnotes={card.footnotes}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </WidgetErrorBoundary>
      )}

      {/* Порядок блоков фиксирован: образование, затем здравоохранение
          (FR-1.3). Это же — ключевые виджеты темы в сводной витрине
          «Все» (плашка туда не идёт). По макету блоки стоят рядом: две колонки от xl, на планшете —
          одна (в 1024 контента ~668 px, таблица в половине не читается).
          Выключенный блок не оставляет пустой колонки — соседний
          занимает первую ячейку сетки (FR-1.3). */}
      {(showEducation || showHealthcare) && (
        <div className="grid grid-cols-1 gap-3.5 xl:grid-cols-2">
        {showEducation && (
          <WidgetErrorBoundary key={`social-education-${selectedKato ?? 'none'}`}>
            <SocialCategoryBlock
              title={t('analytics.social.education.title')}
              icon={GraduationCap}
              columnCategory={t('analytics.social.columnCategory')}
              columns={[
                t('analytics.social.education.columnCount'),
                t('analytics.social.education.columnStudents'),
              ]}
              rows={educationRows}
              period={education.data?.period ?? null}
              source={education.data?.source ?? null}
              note={t('analytics.social.education.note')}
              loading={education.loading}
              drillHint={t('analytics.social.education.drillHint')}
              slug="social-education"
              testId="analytics-social-education"
            />
          </WidgetErrorBoundary>
        )}

        {showHealthcare && (
          <WidgetErrorBoundary key={`social-healthcare-${selectedKato ?? 'none'}`}>
            <SocialCategoryBlock
              title={t('analytics.social.healthcare.title')}
              icon={PlusSquare}
              columnCategory={t('analytics.social.columnCategory')}
              columns={[t('analytics.social.healthcare.columnCount')]}
              rows={healthcareRows}
              period={healthcare.data?.period ?? null}
              source={healthcare.data?.source ?? null}
              note={t('analytics.social.healthcare.note')}
              loading={healthcare.loading}
              drillHint={t('analytics.social.healthcare.drillHint')}
              slug="social-healthcare"
              testId="analytics-social-healthcare"
            />
          </WidgetErrorBoundary>
        )}
        </div>
      )}
    </section>
  );
};
